import React, { useState, useEffect } from "react";
import { db, storage } from "../../firebase/config";
import { collection, query, onSnapshot, addDoc, updateDoc, doc, orderBy, getDocs, getDoc, setDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import Header from "../../components/Header";

const PLANES = {
  starter: { nombre: 'Starter', precioUSD: 29, precioARS: 29000, color: '#00B8FF', descripcion: 'Hasta 3 usuarios · Todos los módulos' },
  advanced: { nombre: 'Advanced', precioUSD: 59, precioARS: 59000, color: '#00D084', descripcion: 'Hasta 6 usuarios · Todos los módulos' },
  pro: { nombre: 'Pro', precioUSD: 99, precioARS: 99000, color: '#B478FF', descripcion: 'Usuarios ilimitados · Soporte prioritario' },
};

const DATOS_BANCARIOS = {
  cuenta: 'CA$ 218-218037244-001',
  alias: 'PAJARO.POETA.REZO',
  cbu: '0340218608218037244018',
  cuil: '20397047564',
  titular: 'Federico Martos',
};

const ESTADOS_CUENTA = {
  al_dia: { label: 'Al día', color: '#00D084', bg: 'rgba(0,208,132,0.15)' },
  proximo_vencer: { label: 'Próximo a vencer', color: '#FFB300', bg: 'rgba(255,179,0,0.15)' },
  vencido: { label: 'Vencido', color: '#FF4757', bg: 'rgba(255,71,87,0.15)' },
  pago_rechazado: { label: 'Pago rechazado', color: '#FF4757', bg: 'rgba(255,71,87,0.15)' },
  suspendido: { label: 'Suspendido', color: '#AEB6C4', bg: 'rgba(174,182,196,0.15)' },
  trial: { label: 'Trial', color: '#00B8FF', bg: 'rgba(0,184,255,0.15)' },
  pago_pendiente: { label: 'Pago pendiente', color: '#FFB300', bg: 'rgba(255,179,0,0.15)' },
};

function calcularEstado(facturacion) {
  if (!facturacion?.fechaVencimiento) return 'al_dia';
  if (facturacion.pagoPendienteAprobacion) return 'pago_pendiente';
  const hoy = new Date();
  const venc = facturacion.fechaVencimiento?.toDate ? facturacion.fechaVencimiento.toDate() : new Date(facturacion.fechaVencimiento);
  const diff = (venc - hoy) / (1000 * 60 * 60 * 24);
  if (facturacion.pagoRechazado) return 'pago_rechazado';
  if (diff < 0) return 'vencido';
  if (diff <= 3) return 'proximo_vencer';
  return 'al_dia';
}

function calcularPrecioConDescuento(plan, moneda, descuento) {
  const base = moneda === 'USD' ? PLANES[plan]?.precioUSD : PLANES[plan]?.precioARS;
  if (!base) return 0;
  if (!descuento?.activo || !descuento?.porcentaje) return base;
  return base * (1 - descuento.porcentaje / 100);
}

export default function Facturacion() {
  const { currentUser } = useAuth();
  const { theme } = useTheme();
  const [clinicas, setClinicas] = useState([]);
  const [facturaciones, setFacturaciones] = useState({});
  const [loading, setLoading] = useState(true);
  const [clinicaSel, setClinicaSel] = useState(null);
  const [modalPago, setModalPago] = useState(false);
  const [modalDescuento, setModalDescuento] = useState(false);
  const [modalPlan, setModalPlan] = useState(false);
  const [modalTipoCambio, setModalTipoCambio] = useState(false);
  const [historialPagos, setHistorialPagos] = useState([]);
  const [expandidoClinica, setExpandidoClinica] = useState(null);
  const [tipoCambio, setTipoCambio] = useState(null);
  const [nuevoTipoCambio, setNuevoTipoCambio] = useState('');
  const [pagosPendientes, setPagosPendientes] = useState([]);

  const [nuevoPago, setNuevoPago] = useState({ monto: '', moneda: 'USD', metodoPago: 'Transferencia', referencia: '', notas: '', pagoRechazado: false });
  const [nuevoDescuento, setNuevoDescuento] = useState({ porcentaje: '', duracionMeses: '', motivo: '', activo: true });
  const [nuevoPlan, setNuevoPlan] = useState({ plan: 'starter', moneda: 'USD', fechaInicio: new Date().toISOString().split('T')[0], duracionMeses: 1 });

  const btnPrimaryStyle = { background: `linear-gradient(135deg, ${theme.accent}, ${theme.accentDark})`, border: 'none', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer', color: '#FFFFFF', fontWeight: '600', fontFamily: "'Montserrat', sans-serif" };
  const btnSecondaryStyle = { background: 'transparent', border: `0.5px solid ${theme.borderInput}`, borderRadius: '8px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer', color: theme.secondary, fontFamily: "'Montserrat', sans-serif" };
  const inputStyle = { width: '100%', padding: '10px 14px', fontSize: '13px', border: `0.5px solid ${theme.borderInput}`, borderRadius: '8px', outline: 'none', color: theme.primary, background: theme.bgInput, boxSizing: 'border-box', fontFamily: "'Montserrat', sans-serif" };
  const labelStyle = { fontSize: '11px', color: theme.secondary, display: 'block', marginBottom: '6px', fontWeight: '500', letterSpacing: '1px' };
  const overlayStyle = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: theme.bgOverlay, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
  const modalStyle = { background: theme.bgModal, borderRadius: '16px', padding: '28px', width: '100%', maxWidth: '480px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.4)', border: `0.5px solid ${theme.border}` };

  useEffect(() => {
    const unsub = onSnapshot(query(collection(db, 'clinicas'), orderBy('nombre')), snap => {
      setClinicas(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'facturacion'), snap => {
      const map = {};
      snap.docs.forEach(d => { map[d.data().clinicaId] = { id: d.id, ...d.data() }; });
      setFacturaciones(map);
    });
    return unsub;
  }, []);

  useEffect(() => {
    const cargarTipoCambio = async () => {
      const snap = await getDoc(doc(db, 'configuracion_sistema', 'tipo_cambio'));
      if (snap.exists()) setTipoCambio(snap.data());
    };
    cargarTipoCambio();
  }, []);

  useEffect(() => {
    const q = query(collection(db, 'pagos_pendientes'), orderBy('fecha', 'desc'));
    const unsub = onSnapshot(q, snap => setPagosPendientes(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    return unsub;
  }, []);

  async function abrirClinica(clinica) {
    setClinicaSel(clinica);
    setExpandidoClinica(clinica.id);
    const snap = await getDocs(query(collection(db, 'pagos'), orderBy('fecha', 'desc')));
    setHistorialPagos(snap.docs.filter(d => d.data().clinicaId === clinica.id).map(d => ({ id: d.id, ...d.data() })));
  }

  async function guardarTipoCambio() {
    if (!nuevoTipoCambio) return;
    await setDoc(doc(db, 'configuracion_sistema', 'tipo_cambio'), {
      valor: parseFloat(nuevoTipoCambio),
      actualizadoEn: new Date(),
      actualizadoPor: currentUser?.email,
    });
    setTipoCambio({ valor: parseFloat(nuevoTipoCambio), actualizadoEn: new Date() });
    setModalTipoCambio(false);
    setNuevoTipoCambio('');
  }

  async function aprobarPago(pago) {
    const fact = facturaciones[pago.clinicaId];
    const fechaPago = new Date();
    const nuevaFechaVenc = new Date();
    nuevaFechaVenc.setMonth(nuevaFechaVenc.getMonth() + 1);

    await addDoc(collection(db, 'pagos'), {
      clinicaId: pago.clinicaId, clinicaNombre: pago.clinicaNombre,
      monto: pago.monto, moneda: pago.moneda,
      metodoPago: 'Transferencia', referencia: pago.referencia || '',
      comprobante: pago.comprobante || null,
      notas: 'Pago aprobado desde comprobante del cliente',
      pagoRechazado: false, fecha: fechaPago,
      registradoPor: currentUser?.email,
    });

    if (fact?.id) {
      await updateDoc(doc(db, 'facturacion', fact.id), {
        ultimoPago: fechaPago, fechaVencimiento: nuevaFechaVenc,
        pagoRechazado: false, pagoPendienteAprobacion: false,
        ultimoMonto: pago.monto, ultimaMoneda: pago.moneda,
      });
    }

    await updateDoc(doc(db, 'pagos_pendientes', pago.id), { estado: 'aprobado', aprobadoPor: currentUser?.email, aprobadoEn: fechaPago });
  }

  async function rechazarPago(pago) {
    await updateDoc(doc(db, 'pagos_pendientes', pago.id), { estado: 'rechazado', rechazadoPor: currentUser?.email, rechazadoEn: new Date() });
    const fact = facturaciones[pago.clinicaId];
    if (fact?.id) await updateDoc(doc(db, 'facturacion', fact.id), { pagoPendienteAprobacion: false, pagoRechazado: true });
  }

  async function registrarPago() {
    if (!nuevoPago.monto || !clinicaSel) return;
    const fact = facturaciones[clinicaSel.id];
    const fechaPago = new Date();
    const nuevaFechaVenc = new Date(fact?.fechaVencimiento?.toDate ? fact.fechaVencimiento.toDate() : new Date());
    nuevaFechaVenc.setMonth(nuevaFechaVenc.getMonth() + 1);
    await addDoc(collection(db, 'pagos'), { clinicaId: clinicaSel.id, clinicaNombre: clinicaSel.nombre, monto: parseFloat(nuevoPago.monto), moneda: nuevoPago.moneda, metodoPago: nuevoPago.metodoPago, referencia: nuevoPago.referencia, notas: nuevoPago.notas, pagoRechazado: nuevoPago.pagoRechazado, fecha: fechaPago, registradoPor: currentUser?.email });
    if (fact?.id) await updateDoc(doc(db, 'facturacion', fact.id), { ultimoPago: fechaPago, fechaVencimiento: nuevaFechaVenc, pagoRechazado: nuevoPago.pagoRechazado, ultimoMonto: parseFloat(nuevoPago.monto), ultimaMoneda: nuevoPago.moneda });
    setHistorialPagos(prev => [{ clinicaId: clinicaSel.id, ...nuevoPago, monto: parseFloat(nuevoPago.monto), fecha: fechaPago }, ...prev]);
    setModalPago(false);
    setNuevoPago({ monto: '', moneda: 'USD', metodoPago: 'Transferencia', referencia: '', notas: '', pagoRechazado: false });
  }

  async function aplicarDescuento() {
    if (!nuevoDescuento.porcentaje || !clinicaSel) return;
    const fact = facturaciones[clinicaSel.id];
    const descuento = { porcentaje: parseFloat(nuevoDescuento.porcentaje), duracionMeses: parseInt(nuevoDescuento.duracionMeses) || 1, motivo: nuevoDescuento.motivo, activo: true, fechaInicio: new Date(), aplicadoPor: currentUser?.email };
    if (fact?.id) await updateDoc(doc(db, 'facturacion', fact.id), { descuento });
    else await addDoc(collection(db, 'facturacion'), { clinicaId: clinicaSel.id, descuento });
    setModalDescuento(false);
    setNuevoDescuento({ porcentaje: '', duracionMeses: '', motivo: '', activo: true });
  }

  async function aplicarPlan() {
    if (!nuevoPlan.plan || !clinicaSel) return;
    const inicio = new Date(nuevoPlan.fechaInicio + 'T12:00:00');
    const vencimientoPlan = new Date(inicio);
    vencimientoPlan.setMonth(vencimientoPlan.getMonth() + parseInt(nuevoPlan.duracionMeses));
    const vencimientoPago = new Date(inicio);
    vencimientoPago.setDate(vencimientoPago.getDate() + 10);
    const fact = facturaciones[clinicaSel.id];
    const data = { clinicaId: clinicaSel.id, plan: nuevoPlan.plan, moneda: nuevoPlan.moneda, fechaInicio: inicio, fechaVencimientoPlan: vencimientoPlan, fechaVencimientoPago: vencimientoPago, fechaVencimiento: vencimientoPlan, duracionMeses: parseInt(nuevoPlan.duracionMeses), actualizadoPor: currentUser?.email, actualizadoEn: new Date() };
    if (fact?.id) await updateDoc(doc(db, 'facturacion', fact.id), data);
    else await addDoc(collection(db, 'facturacion'), data);
    await updateDoc(doc(db, 'clinicas', clinicaSel.id), { plan: nuevoPlan.plan });
    setModalPlan(false);
    setNuevoPlan({ plan: 'starter', moneda: 'USD', fechaInicio: new Date().toISOString().split('T')[0], duracionMeses: 1 });
  }

  async function marcarPagoRechazado(clinicaId) {
    const fact = facturaciones[clinicaId];
    if (fact?.id) await updateDoc(doc(db, 'facturacion', fact.id), { pagoRechazado: true });
  }

  const alertas = clinicas.filter(c => { const f = facturaciones[c.id]; const estado = calcularEstado(f); return ['vencido', 'proximo_vencer', 'pago_rechazado'].includes(estado); });
  const pagosPendientesActivos = pagosPendientes.filter(p => p.estado === 'pendiente');

  return (
    <div style={{ minHeight: '100vh', background: theme.bg, fontFamily: "'Montserrat', sans-serif" }}>
      <Header titulo="Facturación" backLabel="Super Admin" backRuta="/dashboard" />
      <div style={{ padding: '24px', background: theme.bg, display: 'flex', flexDirection: 'column', gap: '20px' }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <div style={{ background: theme.bgCard, borderRadius: '10px', border: `0.5px solid ${theme.border}`, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div>
              <div style={{ fontSize: '11px', color: theme.secondary, fontWeight: '500', letterSpacing: '1px' }}>TIPO DE CAMBIO BNA VENDEDOR</div>
              <div style={{ fontSize: '20px', fontWeight: '700', color: theme.accent }}>
                {tipoCambio ? `$${tipoCambio.valor.toLocaleString('es-AR')}` : '— Sin cargar'}
              </div>
              {tipoCambio?.actualizadoEn && <div style={{ fontSize: '10px', color: theme.secondary }}>Actualizado: {tipoCambio.actualizadoEn?.toDate ? tipoCambio.actualizadoEn.toDate().toLocaleDateString('es-AR') : new Date(tipoCambio.actualizadoEn).toLocaleDateString('es-AR')}</div>}
            </div>
            <button onClick={() => { setNuevoTipoCambio(tipoCambio?.valor || ''); setModalTipoCambio(true); }} style={btnSecondaryStyle}>Actualizar</button>
          </div>
        </div>

        {pagosPendientesActivos.length > 0 && (
          <div style={{ background: theme.warningBg, border: `0.5px solid ${theme.warning}`, borderRadius: '12px', padding: '16px 20px' }}>
            <div style={{ fontSize: '13px', fontWeight: '700', color: theme.warning, marginBottom: '12px' }}>💳 {pagosPendientesActivos.length} pago{pagosPendientesActivos.length > 1 ? 's' : ''} pendiente{pagosPendientesActivos.length > 1 ? 's' : ''} de aprobación</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {pagosPendientesActivos.map(p => {
                const fecha = p.fecha?.toDate ? p.fecha.toDate().toLocaleDateString('es-AR') : '—';
                return (
                  <div key={p.id} style={{ background: theme.bgCard, borderRadius: '8px', padding: '12px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: '600', color: theme.primary }}>{p.clinicaNombre}</div>
                      <div style={{ fontSize: '12px', color: theme.secondary }}>{p.moneda === 'USD' ? 'USD ' : '$'}{parseFloat(p.monto || 0).toLocaleString('es-AR')} · {fecha}</div>
                      {p.referencia && <div style={{ fontSize: '11px', color: theme.secondary }}>Ref: {p.referencia}</div>}
                      {p.comprobante && <a href={p.comprobante} target="_blank" rel="noopener noreferrer" style={{ fontSize: '11px', color: theme.accent, textDecoration: 'none' }}>📎 Ver comprobante</a>}
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button onClick={() => aprobarPago(p)} style={btnPrimaryStyle}>Aprobar</button>
                      <button onClick={() => rechazarPago(p)} style={{ ...btnSecondaryStyle, color: theme.error, borderColor: theme.error }}>Rechazar</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {alertas.length > 0 && (
          <div style={{ background: theme.errorBg, border: `0.5px solid ${theme.error}`, borderRadius: '12px', padding: '16px 20px' }}>
            <div style={{ fontSize: '13px', fontWeight: '700', color: theme.error, marginBottom: '10px' }}>⚠ {alertas.length} clínica{alertas.length > 1 ? 's' : ''} requieren atención</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {alertas.map(c => { const f = facturaciones[c.id]; const estado = calcularEstado(f); const est = ESTADOS_CUENTA[estado]; return (<div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px' }}><span style={{ color: theme.primary, fontWeight: '500' }}>{c.nombre}</span><span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '99px', background: est.bg, color: est.color, fontWeight: '500' }}>{est.label}</span></div>); })}
            </div>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }}>
          {Object.entries(PLANES).map(([key, plan]) => {
            const count = clinicas.filter(c => facturaciones[c.id]?.plan === key).length;
            return (
              <div key={key} style={{ background: theme.bgCard, borderRadius: '12px', border: `0.5px solid ${plan.color}40`, padding: '16px' }}>
                <div style={{ fontSize: '13px', fontWeight: '700', color: plan.color, marginBottom: '4px' }}>{plan.nombre}</div>
                <div style={{ fontSize: '11px', color: theme.secondary, marginBottom: '10px' }}>{plan.descripcion}</div>
                <div style={{ fontSize: '22px', fontWeight: '700', color: theme.primary }}>USD {plan.precioUSD}</div>
                <div style={{ fontSize: '11px', color: theme.secondary }}>
                  {tipoCambio ? `$${(plan.precioUSD * tipoCambio.valor).toLocaleString('es-AR')} ARS` : '— ARS'} · {count} clínica{count !== 1 ? 's' : ''}
                </div>
              </div>
            );
          })}
          <div style={{ background: theme.bgCard, borderRadius: '12px', border: `0.5px solid ${theme.border}`, padding: '16px' }}>
            <div style={{ fontSize: '13px', fontWeight: '700', color: theme.secondary, marginBottom: '4px' }}>Total clínicas activas</div>
            <div style={{ fontSize: '32px', fontWeight: '700', color: theme.accent }}>{clinicas.filter(c => c.estado === 'activa' || c.estado === 'trial').length}</div>
            <div style={{ fontSize: '11px', color: theme.secondary }}>de {clinicas.length} registradas</div>
          </div>
        </div>

        <div style={{ background: theme.bgCard, borderRadius: '12px', border: `0.5px solid ${theme.border}`, overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', borderBottom: `0.5px solid ${theme.border}`, fontSize: '13px', fontWeight: '700', color: theme.secondary, letterSpacing: '1.5px', textTransform: 'uppercase' }}>
            Estado de cuentas — {clinicas.length} clínicas
          </div>
          {loading
            ? <div style={{ padding: '48px', textAlign: 'center', color: theme.secondary }}>Cargando...</div>
            : clinicas.map(c => {
                const f = facturaciones[c.id];
                const estado = calcularEstado(f);
                const est = ESTADOS_CUENTA[estado] || ESTADOS_CUENTA.al_dia;
                const plan = PLANES[f?.plan || c.plan];
                const precioUSD = f?.plan ? calcularPrecioConDescuento(f.plan, 'USD', f?.descuento) : null;
                const precioARS = tipoCambio && precioUSD ? precioUSD * tipoCambio.valor : null;
                const venc = f?.fechaVencimiento?.toDate ? f.fechaVencimiento.toDate() : null;
                const fechaCobro = venc ? new Date(venc.getTime() + 7 * 24 * 60 * 60 * 1000) : null;
                const expandido = expandidoClinica === c.id;

                return (
                  <div key={c.id} style={{ borderBottom: `0.5px solid ${theme.border}` }}>
                    <div onClick={() => { if (expandido) { setExpandidoClinica(null); setClinicaSel(null); } else abrirClinica(c); }} style={{ padding: '14px 20px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: theme.infoBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: '700', color: theme.accent }}>{c.nombre?.[0]}</div>
                        <div>
                          <div style={{ fontSize: '14px', fontWeight: '600', color: theme.primary }}>{c.nombre}</div>
                          <div style={{ fontSize: '12px', color: theme.secondary }}>
                            {plan ? `Plan ${plan.nombre}` : 'Sin plan'} · {venc ? `Vence ${venc.toLocaleDateString('es-AR')}` : 'Sin vencimiento'}
                            {f?.descuento?.activo && <span style={{ marginLeft: '6px', color: theme.success, fontWeight: '600' }}>−{f.descuento.porcentaje}%</span>}
                          </div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        {precioUSD !== null && (
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: '13px', fontWeight: '700', color: theme.primary }}>USD {precioUSD.toFixed(0)}/mes</div>
                            {precioARS && <div style={{ fontSize: '11px', color: theme.secondary }}>${precioARS.toLocaleString('es-AR', { maximumFractionDigits: 0 })} ARS</div>}
                          </div>
                        )}
                        <span style={{ fontSize: '11px', padding: '3px 10px', borderRadius: '99px', fontWeight: '500', background: est.bg, color: est.color }}>{est.label}</span>
                        <span style={{ color: theme.secondary, fontSize: '16px' }}>{expandido ? '▲' : '▼'}</span>
                      </div>
                    </div>

                    {expandido && clinicaSel?.id === c.id && (
                      <div style={{ padding: '0 20px 20px', background: theme.bg }}>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px', paddingTop: '12px' }}>
                          <button onClick={() => setModalPago(true)} style={btnPrimaryStyle}>+ Registrar pago</button>
                          <button onClick={() => setModalPlan(true)} style={btnSecondaryStyle}>Cambiar plan</button>
                          <button onClick={() => setModalDescuento(true)} style={btnSecondaryStyle}>Aplicar descuento</button>
                          <button onClick={() => marcarPagoRechazado(c.id)} style={{ ...btnSecondaryStyle, color: theme.error, borderColor: theme.error }}>Pago rechazado</button>
                        </div>

                        {venc && (
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '10px', marginBottom: '16px' }}>
                            {[
                              { label: 'INICIO DEL PLAN', valor: f?.fechaInicio?.toDate ? f.fechaInicio.toDate().toLocaleDateString('es-AR') : '—' },
                              { label: 'VENCIMIENTO', valor: venc.toLocaleDateString('es-AR') },
                              { label: 'FECHA DE COBRO', valor: fechaCobro ? fechaCobro.toLocaleDateString('es-AR') : '—' },
                            ].map(item => (
                              <div key={item.label} style={{ background: theme.bgCard, padding: '10px 12px', borderRadius: '8px', border: `0.5px solid ${theme.border}` }}>
                                <div style={{ fontSize: '10px', color: theme.secondary, fontWeight: '600', letterSpacing: '1px', marginBottom: '4px' }}>{item.label}</div>
                                <div style={{ fontSize: '13px', fontWeight: '600', color: theme.primary }}>{item.valor}</div>
                              </div>
                            ))}
                          </div>
                        )}

                        {f?.descuento?.activo && (
                          <div style={{ background: theme.successBg, border: `0.5px solid ${theme.success}`, borderRadius: '8px', padding: '10px 14px', marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                              <div style={{ fontSize: '13px', fontWeight: '600', color: theme.success }}>Descuento activo: {f.descuento.porcentaje}% por {f.descuento.duracionMeses} meses</div>
                              {f.descuento.motivo && <div style={{ fontSize: '11px', color: theme.success }}>{f.descuento.motivo}</div>}
                            </div>
                            <button onClick={async () => { await updateDoc(doc(db, 'facturacion', f.id), { 'descuento.activo': false }); }} style={{ ...btnSecondaryStyle, fontSize: '11px', padding: '3px 8px', color: theme.error, borderColor: theme.error }}>Quitar</button>
                          </div>
                        )}

                        <div style={{ fontSize: '11px', fontWeight: '700', color: theme.secondary, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '10px' }}>Historial de pagos</div>
                        {historialPagos.length === 0
                          ? <div style={{ fontSize: '13px', color: theme.secondary, padding: '16px', textAlign: 'center', background: theme.bgCard, borderRadius: '8px', border: `0.5px solid ${theme.border}` }}>Sin pagos registrados</div>
                          : <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                              {historialPagos.map((p, i) => {
                                const fechaPago = p.fecha?.toDate ? p.fecha.toDate().toLocaleDateString('es-AR') : '—';
                                return (
                                  <div key={i} style={{ background: theme.bgCard, borderRadius: '8px', padding: '10px 14px', border: `0.5px solid ${p.pagoRechazado ? theme.error : theme.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                                    <div>
                                      <div style={{ fontSize: '13px', fontWeight: '500', color: theme.primary }}>{p.metodoPago} · {fechaPago}</div>
                                      {p.referencia && <div style={{ fontSize: '11px', color: theme.secondary }}>Ref: {p.referencia}</div>}
                                      {p.comprobante && <a href={p.comprobante} target="_blank" rel="noopener noreferrer" style={{ fontSize: '11px', color: theme.accent, textDecoration: 'none' }}>📎 Ver comprobante</a>}
                                      {p.notas && <div style={{ fontSize: '11px', color: theme.secondary }}>{p.notas}</div>}
                                    </div>
                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                      {p.pagoRechazado && <span style={{ fontSize: '11px', padding: '2px 6px', borderRadius: '99px', background: theme.errorBg, color: theme.error, fontWeight: '500' }}>Rechazado</span>}
                                      <span style={{ fontSize: '15px', fontWeight: '700', color: p.pagoRechazado ? theme.error : theme.success }}>{p.moneda === 'USD' ? 'USD ' : '$'}{parseFloat(p.monto || 0).toLocaleString('es-AR')}</span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                        }
                      </div>
                    )}
                  </div>
                );
              })
          }
        </div>
      </div>

      {modalTipoCambio && (
        <div style={overlayStyle}>
          <div style={{ ...modalStyle, maxWidth: '360px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <span style={{ fontSize: '16px', fontWeight: '600', color: theme.primary }}>Tipo de cambio BNA</span>
              <button onClick={() => setModalTipoCambio(false)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: theme.secondary }}>×</button>
            </div>
            <div style={{ fontSize: '12px', color: theme.secondary, marginBottom: '16px' }}>Ingresá el tipo de cambio vendedor oficial del Banco de la Nación Argentina del día.</div>
            <div><label style={labelStyle}>VALOR EN PESOS (ARS) POR 1 USD</label><input type="number" value={nuevoTipoCambio} onChange={e => setNuevoTipoCambio(e.target.value)} placeholder="Ej: 1050" style={inputStyle} autoFocus /></div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '20px' }}>
              <button onClick={guardarTipoCambio} style={btnPrimaryStyle}>Guardar</button>
              <button onClick={() => setModalTipoCambio(false)} style={btnSecondaryStyle}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {modalPago && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <span style={{ fontSize: '16px', fontWeight: '600', color: theme.primary }}>Registrar pago — {clinicaSel?.nombre}</span>
              <button onClick={() => setModalPago(false)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: theme.secondary }}>×</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div><label style={labelStyle}>MONTO *</label><input type="number" value={nuevoPago.monto} onChange={e => setNuevoPago({...nuevoPago, monto: e.target.value})} style={inputStyle} /></div>
                <div><label style={labelStyle}>MONEDA</label>
                  <select value={nuevoPago.moneda} onChange={e => setNuevoPago({...nuevoPago, moneda: e.target.value})} style={inputStyle}>
                    <option value="USD">USD</option>
                    <option value="ARS">ARS</option>
                  </select>
                </div>
              </div>
              <div><label style={labelStyle}>MÉTODO DE PAGO</label>
                <select value={nuevoPago.metodoPago} onChange={e => setNuevoPago({...nuevoPago, metodoPago: e.target.value})} style={inputStyle}>
                  <option value="Transferencia">Transferencia</option>
                  <option value="MercadoPago">MercadoPago</option>
                  <option value="Tarjeta">Tarjeta</option>
                  <option value="Efectivo">Efectivo</option>
                </select>
              </div>
              <div><label style={labelStyle}>REFERENCIA / COMPROBANTE</label><input value={nuevoPago.referencia} onChange={e => setNuevoPago({...nuevoPago, referencia: e.target.value})} placeholder="N° transferencia, ID pago..." style={inputStyle} /></div>
              <div><label style={labelStyle}>NOTAS INTERNAS</label><textarea value={nuevoPago.notas} onChange={e => setNuevoPago({...nuevoPago, notas: e.target.value})} rows={2} style={{ ...inputStyle, resize: 'vertical' }} /></div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input type="checkbox" checked={nuevoPago.pagoRechazado} onChange={e => setNuevoPago({...nuevoPago, pagoRechazado: e.target.checked})} id="rechazado" />
                <label htmlFor="rechazado" style={{ fontSize: '13px', color: theme.error, cursor: 'pointer' }}>Marcar como pago rechazado</label>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '20px' }}>
              <button onClick={registrarPago} style={btnPrimaryStyle}>Guardar pago</button>
              <button onClick={() => setModalPago(false)} style={btnSecondaryStyle}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {modalDescuento && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <span style={{ fontSize: '16px', fontWeight: '600', color: theme.primary }}>Aplicar descuento — {clinicaSel?.nombre}</span>
              <button onClick={() => setModalDescuento(false)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: theme.secondary }}>×</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div><label style={labelStyle}>DESCUENTO %</label><input type="number" min="1" max="100" value={nuevoDescuento.porcentaje} onChange={e => setNuevoDescuento({...nuevoDescuento, porcentaje: e.target.value})} placeholder="Ej: 20" style={inputStyle} /></div>
                <div><label style={labelStyle}>DURACIÓN (MESES)</label><input type="number" min="1" value={nuevoDescuento.duracionMeses} onChange={e => setNuevoDescuento({...nuevoDescuento, duracionMeses: e.target.value})} placeholder="Ej: 3" style={inputStyle} /></div>
              </div>
              <div><label style={labelStyle}>MOTIVO</label><input value={nuevoDescuento.motivo} onChange={e => setNuevoDescuento({...nuevoDescuento, motivo: e.target.value})} placeholder="Ej: Descuento lanzamiento..." style={inputStyle} /></div>
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '20px' }}>
              <button onClick={aplicarDescuento} style={btnPrimaryStyle}>Aplicar descuento</button>
              <button onClick={() => setModalDescuento(false)} style={btnSecondaryStyle}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {modalPlan && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <span style={{ fontSize: '16px', fontWeight: '600', color: theme.primary }}>Cambiar plan — {clinicaSel?.nombre}</span>
              <button onClick={() => setModalPlan(false)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: theme.secondary }}>×</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={labelStyle}>PLAN</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {Object.entries(PLANES).map(([key, p]) => (
                    <button key={key} onClick={() => setNuevoPlan({...nuevoPlan, plan: key})} style={{ flex: 1, padding: '10px', borderRadius: '8px', border: nuevoPlan.plan === key ? 'none' : `0.5px solid ${theme.borderInput}`, background: nuevoPlan.plan === key ? p.color : 'transparent', color: nuevoPlan.plan === key ? '#FFFFFF' : theme.secondary, cursor: 'pointer', fontFamily: "'Montserrat', sans-serif", fontSize: '12px', fontWeight: '600' }}>
                      {p.nombre}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ background: theme.bgCard, padding: '12px', borderRadius: '8px', border: `0.5px solid ${theme.border}` }}>
                <div style={{ fontSize: '12px', color: theme.secondary, marginBottom: '4px' }}>{PLANES[nuevoPlan.plan]?.descripcion}</div>
                <div style={{ fontSize: '16px', fontWeight: '700', color: theme.primary }}>
                  USD {PLANES[nuevoPlan.plan]?.precioUSD}
                  {tipoCambio && <span style={{ fontSize: '13px', color: theme.secondary }}> · ${(PLANES[nuevoPlan.plan]?.precioUSD * tipoCambio.valor).toLocaleString('es-AR', { maximumFractionDigits: 0 })} ARS</span>}
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div><label style={labelStyle}>MONEDA DE COBRO</label>
                  <select value={nuevoPlan.moneda} onChange={e => setNuevoPlan({...nuevoPlan, moneda: e.target.value})} style={inputStyle}>
                    <option value="USD">USD</option>
                    <option value="ARS">ARS</option>
                  </select>
                </div>
                <div><label style={labelStyle}>DURACIÓN (MESES)</label><input type="number" min="1" value={nuevoPlan.duracionMeses} onChange={e => setNuevoPlan({...nuevoPlan, duracionMeses: e.target.value})} style={inputStyle} /></div>
              </div>
              <div><label style={labelStyle}>FECHA DE INICIO</label><input type="date" value={nuevoPlan.fechaInicio} onChange={e => setNuevoPlan({...nuevoPlan, fechaInicio: e.target.value})} style={inputStyle} /></div>
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '20px' }}>
              <button onClick={aplicarPlan} style={btnPrimaryStyle}>Confirmar plan</button>
              <button onClick={() => setModalPlan(false)} style={btnSecondaryStyle}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}